import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tenant,
  CreateTenantRequest,
  TenantStatistics,
  SuperAdminStats,
} from "../../types";
import { apiService } from "../../services/api";
import ConfirmDialog from "../common/ConfirmDialog";
import EditTenantModal from "./EditTenantModal";
import TenantMachineLimitCard from "./TenantMachineLimitCard";

interface TenantManagerProps {
  onTenantSelect?: (tenant: Tenant) => void;
}

export default function TenantManager({ onTenantSelect }: TenantManagerProps) {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tenantStats, setTenantStats] = useState<TenantStatistics | null>(null);
  const [superAdminStats, setSuperAdminStats] =
    useState<SuperAdminStats | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: "danger" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Filters and pagination
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    sort_by: "created_at",
    sort_order: "desc" as "asc" | "desc",
    page: 1,
    per_page: 10,
  });

  const [pagination, setPagination] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
  });

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getTenants(filters);
      setTenants(response.data);
      setPagination({
        total: response.pagination.total,
        current_page: response.pagination.current_page,
        per_page: response.pagination.per_page,
      });
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadSuperAdminStats = useCallback(async () => {
    try {
      const stats = await apiService.getSuperAdminStatistics();
      setSuperAdminStats(stats);
    } catch (err: any) {
      console.error("Erro ao carregar estatísticas:", err);
    }
  }, []);

  useEffect(() => {
    loadTenants();
    loadSuperAdminStats();
  }, [loadTenants, loadSuperAdminStats]);

  const loadTenantStats = async (tenantId: string) => {
    try {
      const stats = await apiService.getTenantStatistics(tenantId);
      setTenantStats(stats);
    } catch (err: any) {
      console.error("Erro ao carregar estatísticas da empresa:", err);
    }
  };

  const handleCreateTenant = async (
    data: CreateTenantRequest,
    files?: {
      logo?: File;
      background?: File;
      contract?: File;
      contractExpiration?: string;
    }
  ) => {
    try {
      const response = await apiService.createTenant(data);
      const tenantId = response.data.tenant.id;

      if (files) {
        if (files.logo) {
          await apiService.uploadTenantLogo(tenantId, files.logo);
        }
        if (files.background) {
          await apiService.uploadTenantBackground(tenantId, files.background);
        }
        if (files.contract) {
          await apiService.uploadTenantContract(
            tenantId,
            files.contract,
            files.contractExpiration
          );
        }
      }

      setShowCreateForm(false);
      loadTenants();
      loadSuperAdminStats();
    } catch (err: any) {
      throw new Error(err.response?.data?.message || "Erro ao criar empresa");
    }
  };

  const handleUpdateTenant = async (
    tenantId: string,
    data: any,
    files?: {
      logo?: File;
      background?: File;
      contract?: File;
      contractExpiration?: string;
    }
  ) => {
    try {
      await apiService.updateTenant(tenantId, data);

      if (files) {
        if (files.logo) {
          await apiService.uploadTenantLogo(tenantId, files.logo);
        }
        if (files.background) {
          await apiService.uploadTenantBackground(tenantId, files.background);
        }
        if (files.contract) {
          await apiService.uploadTenantContract(
            tenantId,
            files.contract,
            files.contractExpiration
          );
        }
      }

      setEditingTenant(null);
      loadTenants();
      loadSuperAdminStats();
      if (selectedTenant?.id === tenantId) {
        loadTenantStats(tenantId);
      }
    } catch (err: any) {
      throw new Error(
        err.response?.data?.message || "Erro ao atualizar empresa"
      );
    }
  };

  const handleDeactivateTenant = async (tenantId: string) => {
    const tenant = tenants.find((t) => t.id === tenantId);
    if (!tenant) return;

    setConfirmDialog({
      isOpen: true,
      title: "Confirmar desativação",
      message: `Tem certeza que deseja desativar a empresa "${tenant.name}"? O acesso será suspenso para todos os usuários desta empresa.`,
      type: "warning",
      onConfirm: async () => {
        try {
          await apiService.updateTenantStatus(tenantId, "inactive");
          loadTenants();
          loadSuperAdminStats();
          if (selectedTenant?.id === tenantId) {
            // Update selected tenant status locally or reload
            loadTenantStats(tenantId);
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        } catch (err: any) {
          setError(err.response?.data?.message || "Erro ao desativar empresa");
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }
      },
    });
  };

  const handleTenantClick = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    loadTenantStats(tenant.id);
    onTenantSelect?.(tenant);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      case "suspended":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "Ativo";
      case "inactive":
        return "Inativo";
      case "suspended":
        return "Suspenso";
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Statistics */}
      {superAdminStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Total de Empresas
            </h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {superAdminStats.tenants?.total || 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Empresas Ativas
            </h3>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {superAdminStats.tenants?.active || 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Total de Usuários
            </h3>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {superAdminStats.users?.total || 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Total de Máquinas
            </h3>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {superAdminStats.machines?.total || 0}
            </p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value, page: 1 })
              }
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value, page: 1 })
              }
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">Todos os Status</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="suspended">Suspenso</option>
            </select>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800"
          >
            Nova Empresa
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lista de Empresas */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Lista de Empresas
            </h3>

            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  Carregando...
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {tenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    onClick={() => handleTenantClick(tenant)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedTenant?.id === tenant.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400"
                        : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {tenant.name}
                        </h4>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{tenant.users_count || 0} usuários</span>
                          <span>{tenant.machines_count || 0} máquinas</span>
                          <span>{tenant.alerts_count || 0} alertas</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            tenant.status
                          )}`}
                        >
                          {getStatusText(tenant.status)}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTenant(tenant);
                            }}
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            Editar
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeactivateTenant(tenant.id);
                            }}
                            className="text-xs px-2 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          >
                            Desativar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {tenants.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Nenhuma empresa encontrada
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {pagination.total > pagination.per_page && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrando{" "}
                  {(pagination.current_page - 1) * pagination.per_page + 1} a{" "}
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total
                  )}{" "}
                  de {pagination.total} resultados
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setFilters({ ...filters, page: filters.page - 1 })
                    }
                    disabled={filters.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() =>
                      setFilters({ ...filters, page: filters.page + 1 })
                    }
                    disabled={
                      filters.page >=
                      Math.ceil(pagination.total / pagination.per_page)
                    }
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detalhes da Empresa */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Detalhes da Empresa
            </h3>

            {selectedTenant ? (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {selectedTenant.name}
                  </h4>
                  <span
                    className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      selectedTenant.status
                    )}`}
                  >
                    {getStatusText(selectedTenant.status)}
                  </span>
                </div>

                {tenantStats && (
                  <div className="space-y-4">
                    {/* Machine Limit Card */}
                    <TenantMachineLimitCard
                      tenant={selectedTenant}
                      onUpdate={() => {
                        loadTenants();
                        loadTenantStats(selectedTenant.id);
                      }}
                    />

                    <div>
                      <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Usuários
                      </h5>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div>Total: {tenantStats.users?.total || 0}</div>
                        <div>Ativos: {tenantStats.users?.active || 0}</div>
                        <div>Admins: {tenantStats.users?.admins || 0}</div>
                        <div>
                          Usuários: {tenantStats.users?.regular_users || 0}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Máquinas
                      </h5>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div>Total: {tenantStats.machines?.total || 0}</div>
                        <div className="text-green-600 dark:text-green-400">
                          Online: {tenantStats.machines?.online || 0}
                        </div>
                        <div className="text-red-600 dark:text-red-400">
                          Offline: {tenantStats.machines?.offline || 0}
                        </div>
                        <div className="text-yellow-600 dark:text-yellow-400">
                          Aviso: {tenantStats.machines?.warning || 0}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Alertas
                      </h5>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div>Total: {tenantStats.alerts?.total || 0}</div>
                        <div>Abertos: {tenantStats.alerts?.open || 0}</div>
                        <div>
                          Confirmados: {tenantStats.alerts?.acknowledged || 0}
                        </div>
                        <div>
                          Resolvidos: {tenantStats.alerts?.resolved || 0}
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <div>
                          Críticos:{" "}
                          {tenantStats.alerts?.by_severity?.critical || 0}
                        </div>
                        <div>
                          Altos: {tenantStats.alerts?.by_severity?.high || 0}
                        </div>
                        <div>
                          Médios: {tenantStats.alerts?.by_severity?.medium || 0}
                        </div>
                        <div>
                          Baixos: {tenantStats.alerts?.by_severity?.low || 0}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                      <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Visualizar Detalhes
                      </h5>
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          onClick={() =>
                            navigate(
                              `/super-admin/tenants/${selectedTenant.id}/machines`
                            )
                          }
                          className="flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          <span className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            Ver Máquinas
                          </span>
                        </button>

                        <button
                          onClick={() =>
                            navigate(
                              `/super-admin/tenants/${selectedTenant.id}/users`
                            )
                          }
                          className="flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          <span className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                            Ver Usuários
                          </span>
                        </button>

                        <button
                          onClick={() =>
                            navigate(
                              `/super-admin/tenants/${selectedTenant.id}/alerts`
                            )
                          }
                          className="flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          <span className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            Ver Alertas
                          </span>
                        </button>

                        <button
                          onClick={() =>
                            navigate(
                              `/super-admin/tenants/${selectedTenant.id}/branches`
                            )
                          }
                          className="flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          <span className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            Ver Filiais
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Selecione uma empresa para ver os detalhes
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateForm && (
        <CreateTenantForm
          onSubmit={handleCreateTenant}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* Edit Tenant Modal */}
      {editingTenant && (
        <EditTenantModal
          tenant={editingTenant}
          onSubmit={handleUpdateTenant}
          onCancel={() => setEditingTenant(null)}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </div>
  );
}

interface CreateTenantFormProps {
  onSubmit: (
    data: CreateTenantRequest,
    files?: {
      logo?: File;
      background?: File;
      contract?: File;
      contractExpiration?: string;
    }
  ) => Promise<void>;
  onCancel: () => void;
}

function CreateTenantForm({ onSubmit, onCancel }: CreateTenantFormProps) {
  const [formData, setFormData] = useState<CreateTenantRequest>({
    name: "",
    status: "active",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [contractExpiration, setContractExpiration] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.admin_password !== passwordConfirmation) {
      setError("As senhas não coincidem");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onSubmit(formData, {
        logo: logoFile || undefined,
        background: backgroundFile || undefined,
        contract: contractFile || undefined,
        contractExpiration: contractExpiration || undefined,
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Criar Nova Empresa
          </h3>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome da Empresa
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status Inicial
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as any })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>

            <hr className="my-4 dark:border-gray-600" />
            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              Dados do Administrador
            </h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome do Administrador
              </label>
              <input
                type="text"
                required
                value={formData.admin_name}
                onChange={(e) =>
                  setFormData({ ...formData, admin_name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email do Administrador
              </label>
              <input
                type="email"
                required
                value={formData.admin_email}
                onChange={(e) =>
                  setFormData({ ...formData, admin_email: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Telefone (opcional)
              </label>
              <input
                type="tel"
                value={formData.admin_phone || ""}
                onChange={(e) =>
                  setFormData({ ...formData, admin_phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="(00) 00000-0000"
              />
            </div>

            <hr className="my-4 dark:border-gray-600" />
            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              Endereço (opcional)
            </h4>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rua
                </label>
                <input
                  type="text"
                  value={formData.address_street || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, address_street: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Número
                </label>
                <input
                  type="text"
                  value={formData.address_number || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, address_number: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cidade
                </label>
                <input
                  type="text"
                  value={formData.address_city || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, address_city: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estado (UF)
                </label>
                <input
                  type="text"
                  maxLength={2}
                  value={formData.address_state || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address_state: e.target.value.toUpperCase(),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="SP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CEP
                </label>
                <input
                  type="text"
                  value={formData.address_zip || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, address_zip: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="00000-000"
                />
              </div>
            </div>

            <hr className="my-4 dark:border-gray-600" />
            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              Senha de Acesso
            </h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Senha do Administrador
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={formData.admin_password}
                onChange={(e) =>
                  setFormData({ ...formData, admin_password: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirmar Senha
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <hr className="my-4 dark:border-gray-600" />
            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              Arquivos e Contrato (Opcional)
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Logo da Empresa
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setLogoFile(e.target.files ? e.target.files[0] : null)
                  }
                  className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Imagem de Fundo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setBackgroundFile(e.target.files ? e.target.files[0] : null)
                  }
                  className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contrato (PDF)
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    setContractFile(e.target.files ? e.target.files[0] : null)
                  }
                  className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vencimento do Contrato
                </label>
                <input
                  type="date"
                  value={contractExpiration}
                  onChange={(e) => setContractExpiration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-700 dark:hover:bg-blue-800"
              >
                {loading ? "Criando..." : "Criar Empresa"}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
